import { CollectionTarget } from "@ciaopeople/cp-shatsu/DomHelper";
import { EventHandler, SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { RemoteStorageClient } from "@ciaopeople/cp-shatsu/Storage";
import { inEnum, isArray, isDefined, isFunction, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { RESTAPIEventNames } from "../api/RESTAPIEvents.js";
import { EmailLoginAskResponse, RESTUserServices } from "../api/RESTUserServices.js";
import { PropertyConfig } from "../config/PropertyConfig.js";
import { BasicCTALogicImplPromiseLocked } from "../cta/impl/BasicCTALogicImpl.js";
import { UserCTALogic } from "../cta/UserCTALogic.js";
import { SdkGuiController } from "../gui/guiControllers/SdkGuiController.js";
import { getAuthProviderButtonsToShow } from "../gui/utils.js";
import { SdkModuleAutoloadManager } from "../modularization/SdkModuleAutoloadManager.js";
import { BarrierDisplayMode, LoginExtent } from "./aaa/AAABarrierWithSource.js";
import { LoggedInBarrier } from "./aaa/impl/LoggedInBarrier.js";
import { AbstractClientSdk7 } from "./AbstractClientSdk7.js";
import { ClientSdkPublicInterface, LinkTarget, MembershipDialogFlag, Modules, OldFormatParseOptions, ParseOptionGroup, isYearSub, isMonthSub, MembershipFlagsBasicConf, BuyMembershipOptions, isDiscount, isFreeTrial, OTPVerificationResponse, MailFixDialogOutcome, isOTPVerificationResponse } from "./ClientSdkPublicInterface.js";
import { BookmarkParseAndAttachModule } from "./modules/BookmarkParseAndAttachModule.js";
import { FollowParseAndAttachModule } from "./modules/FollowParseAndAttachModule.js";
import { ActionableEvents } from "./modules/ParseAndAttachModule.js";
import { FOLLOW_SLIDER_FOUND_EVT, SliderParseAndAttachModule } from "./modules/SliderParseAndAttachModule.js";

import { UserActionIdentifier } from "@api-commons/api/commondata/UserActionError.js";
import { isDismissedCTA } from "@api-commons/cta/impl/EvaluatedActionExecutor.js";
import { isGift, MembershipType } from "@api-commons/membership/MembershipController.js";
import { Action } from "@ciaopeople/cp-shatsu/EventHandler/event-handler";
import { Response } from "@ciaopeople/cp-shatsu/HTTP";
import { Ask1stPartyEmailLoginOperation } from "../api/APIClientInterface.js";
import { Ask1stPartyEmailAuthData, EmailLoginSourceContext, UserFullInfo } from "../api/types/UserDataTypes.js";
import { PrompLoginCustomParameters } from "../login/direct_providers/LoginProvider.js";

import { ManualAction } from "../cta/EvaluatedAction.js";
import { NewsletterParseAndAttachModule } from "./modules/NewsletterParseAndAttachModule.js";
import { SubscriptionCallbackData } from "./modules/SdkHandledHTMLElement.js";
import { SessionObject } from "@api-commons/session/SessionObject.js";
import { AuthProviderIdentifier } from "@api-commons/login/AuthProviderIdentifier.js";
import { UserRole } from "@api-commons/login/UserRole.js";
import { CTA_NAME, CTA_OUTCOME, CTAStatsDetails } from "@api-commons/stats/CTATypes.js";
import { SharedGuiControls } from "../gui/SharedGuiControls.js";
import { isLoggedInSession } from "@api-commons/session/StorableSession.js";

type MessageDictionary = any;


export class ClientSdk7 extends 
    AbstractClientSdk7 implements  ClientSdkPublicInterface, EventHandler {

    private eventHandler: EventHandler = new SimpleEventHandler();

    private ctaLogic: UserCTALogic;
    private modules: Modules;
    private loginBarrierRestServices: LoggedInBarrier<RESTUserServices>;

    constructor(
        config: PropertyConfig,
        public readonly dictionary: MessageDictionary,
        public readonly autoloader: SdkModuleAutoloadManager
    ) {
        super(config, false, {});
        this.serverInit();
    }
    

    private supportsMembership(): boolean {
        return !!this.config.membership?.enable;
    }

    protected beforeServerInit(): void {
        
        let reauthResolve;
        const reauthPromise = new Promise<void>(resolve => {
            reauthResolve = resolve;
        });

        this.ctaLogic = new BasicCTALogicImplPromiseLocked(this, new RemoteStorageClient(this.proxy), this.statsCollector, reauthPromise);

        this.onReady(_api => {
            this.ctaLogic.evaluateCTA();
        });

        this.loginManager.onLoginStatusChange(
            (s) => {
                this.trigger("loginStatusChanged", s);
                this.getMeCache = null;
                this.deviceIdProvider.retrieveOrGenerateDeviceId();
                this.nativeapp.sessionChange( s, this.detectedWebview.platform.android ? "android" : "ios" );
                if (!this.isReady()) {
                    this.setReady(this);
                    this.membershipReauthRoutine().then(
                        res => {
                            this.logger.debug("Reauth evaluation is ", res);
                            if (!res) {
                                reauthResolve();
                            }
                        }
                    )
                }
            }
        );
    }


    public async verificationWithOTPCode(challenge: string, code: string): Promise<OTPVerificationResponse> {
        const response =  await super.verificationWithOTPCode(challenge, code);    
        this.getMeCache = null;
        return response;
    }


    public onUnderfollowShown(fun: () => void): void {
        this.ctaLogic.onUnderfollowShown(fun);
    }

    public onCTAComplete(fun: () => void) {
        this.ctaLogic.onCTAComplete(fun);
    }
    public onCTADismiss(fun: () => void) {
        this.ctaLogic.onCTADismiss(fun);
    }

    signalNonUserDismiss(fun: () => void): void {

    }
    protected afterServerInit(): void {
        this.loginBarrierRestServices = new LoggedInBarrier(this, 
            this.api as RESTUserServices, 
            this.urlHandler, 
            this.sessionProxy, 
            this.config.loginManager.barrierDisplayMode
        );
        
        this.modules = {
            follows: new FollowParseAndAttachModule(this.loginBarrierRestServices, this.api),
            sliders: new SliderParseAndAttachModule(this.api)
        };
        if (this.config.support.bookmarks) {
            this.modules.bookmark = new BookmarkParseAndAttachModule(this.loginBarrierRestServices, this.api);
        }
        if (this.config.support.newsletters) {
            //?todo: Create a barrier for membership and pass it
            this.modules.newsletters = new NewsletterParseAndAttachModule(this.loginBarrierRestServices, this.api);
        }

        for(const k in this.modules) {
            this.successSubject.add(this.modules[k].success);
            this.errorSubject.add(this.modules[k].errors);
        }

        this.modules.follows.onTopicFollowStatusChange((fwStatus) => {
            this.trigger(RESTAPIEventNames.TOPIC_FOLLOW_STATUS_CHANGE, fwStatus);
        });

        if(this.modules.bookmark) {
            this.modules.bookmark.onArticleSaveStatusChange((saveData) => {
                this.trigger(RESTAPIEventNames.ARTICLE_SAVE_STATUS_CHANGE, saveData);
            });
        }
        
        if(this.modules.newsletters) {
            this.modules.newsletters.onNewsletterSubscriptionStatusChange((subscribeNews) => {
                this.trigger(RESTAPIEventNames.NEWSLETTER_SUBSCRIPTION_STATUS_CHANGE, subscribeNews);
            });
        }

        this.modules.sliders.onFollowSliderFound((sliderFound) => {
            this.trigger(FOLLOW_SLIDER_FOUND_EVT, sliderFound);
        });

        this.on("loginStatusChanged",(e,s) => {
            if(this.modules?.follows) {
                this.modules.follows.refreshElementsStatus();
            }
            if(this.modules?.newsletters) {
                this.modules.newsletters.refreshElementsStatus();
            }
            if(this.modules?.bookmark) {
                this.modules.bookmark.refreshElementsStatus();
            }
        });
    }
  
    public getDictionary(label: string, ...args:string[]): string {
        const fields = label.split(".");
        let nav: MessageDictionary = this.dictionary;
        const otherargs: string[] =  args;
        let txt = null;
        for (let i=0; i<fields.length && txt == null; i++) {
            if(isObject(nav) && fields[i] in nav) {
                nav = nav[fields[i]];
                if(i == fields.length - 1) {
                    if(isFunction(nav)) {
                        txt = nav.apply(this.dictionary, otherargs);
                    }
                    else {
                        txt = nav;
                    }
                }
            }
            else txt = "";
        }
        if(!isString(txt)) txt = "";
        return txt;
    }


    //%% SPARSE METHOD SECTION
    markPopupShowed(): void {
        
    }
    
    markPopupClosed(): void {
        
        
    }

    //%% ENDOF SPARSE METHOD SECTION


    //%% PARSE SECTION

    public parseAndAttachFollows(c: CollectionTarget): Promise<void> {
        if(this.modules.follows) return this.modules.follows.parseAndAttach(c);
        else return Promise.resolve();
    }
    

    public parseAndAttachNewsletterSubscription(c: CollectionTarget): Promise<void> {
        if(this.modules.newsletters) return this.modules.newsletters.parseAndAttach(c);
        else return Promise.resolve();
    }

    public parseAndAttachSliders(c: CollectionTarget): Promise<void> {
        if(this.modules.sliders) return this.modules.sliders.parseAndAttach(c);
        else return Promise.resolve();
    }

    public parseAndAttachSave(c: CollectionTarget): Promise<void> {
        return this.parseAndAttachBookmark(c);
    }

    public parseAndAttachBookmark(c: CollectionTarget): Promise<void> {
        if(this.modules.bookmark) return this.modules.bookmark.parseAndAttach(c);
        else return Promise.resolve();
    }
   
    public parse(targets: CollectionTarget|CollectionTarget[], options: OldFormatParseOptions, triggeringEvent?: ActionableEvents): Promise<void> {
        const newOptions: ParseOptionGroup = {};
        if(options && options.follows && isDefined(targets)) {
            const useTarget = isArray(targets)?targets[0]:targets;
            newOptions.follows = {
                enable: true,
                target: useTarget
            };
            if(triggeringEvent) newOptions.follows.event = triggeringEvent;
            if(isFunction(options.unfollowConfirm))  newOptions.follows.confirmCallback = options.unfollowConfirm;
        }
        

        if(options && options.sliders && isArray(targets) && targets.length > 1) {
            const useTarget = targets[1];
            newOptions.sliders = {
                enable: true,
                target: useTarget
            };
        }
        if(options && options.articlesave && isArray(targets) && targets.length > 2) {
            const useTarget = targets[2];
            newOptions.bookmark = {
                enable: true,
                target: useTarget
            };
        }

        if(options && options.newsletters && isArray(targets) && targets.length > 3) {
            const useTarget = targets[3];
            newOptions.newsletters = {
                enable: true,
                target: useTarget
            }
        }   

        return this.parseFunctionalities(newOptions);
    }


    public parseFunctionalities(opts: ParseOptionGroup): Promise<void> {
        
        const promises: Promise<void>[] = [];

        if(opts.follows && opts.follows.enable) {
            promises.push(this.parseAndAttachFollows(opts.follows.target));
        }
        if(opts.bookmark && opts.bookmark.enable) {
            promises.push(this.parseAndAttachBookmark(opts.bookmark.target));
        }
        if(opts.sliders && opts.sliders.enable) {
            promises.push(this.parseAndAttachSliders(opts.sliders.target));
        }
        if(opts.newsletters && opts.newsletters.enable) {
            promises.push(this.parseAndAttachNewsletterSubscription(opts.newsletters.target))
        }

        return Promise.all(promises).then();
    }

    //%%ENDOF PARSE SECTION


    //%% DELEGATE SECTION

    //%% UrlHandler DELEGATE SECTION
    getProperUrl(url: string): Promise<string> {
        return this.urlHandler.getProperUrl(url);
    }
    toDynamicLink(url: string): string {
        return this.urlHandler.toDynamicLink(url);
    }
    openUrl(url: string, sameTarget?: boolean): void {
        return this.urlHandler.openUrl(url, sameTarget);
    }
    openSettingsUrl(settingsPage: string, utm_source?: string, utm_medium?: string, otherParams?: { [k: string]: string; }): void {
        return this.urlHandler.openSettingsUrl(settingsPage, utm_source, utm_medium, otherParams);
    }
    getSettingsUrl(settingsPage: string, utm_source?: string, utm_medium?: string, otherParams?: { [k: string]: string; }): string {
        return this.urlHandler.getSettingsUrl(settingsPage, utm_source, utm_medium, otherParams);
    }
    isServiceUrl(url: string): boolean {
        return this.urlHandler.isServiceUrl(url);
    }
    buildServiceUrl(label: string, params?: { [k: string]: string; }): string {
        return this.urlHandler.buildServiceUrl(label, params);
    }


    protected getSharedGuiControls(): Promise<SharedGuiControls> {
        return this.autoloader.getGui();
    }
   
    
    getGui(): Promise<SdkGuiController> {
        return this.autoloader.getGui();
    }

    // %% Event Handler Delegate
    on(eventName: string, callback: Action): any {
        this.eventHandler.on(eventName, callback);
    }
    
    off(evtOrAct: string | Action, act?: Action): any {
        this.eventHandler.off(evtOrAct, act);
    }
    
    trigger(eventName: string, data: any): void {
        this.eventHandler.trigger(eventName, data);
    }
    
    one(eventName: string, callback: Action): void {
        this.eventHandler.one(eventName, callback);
    }
    
    //%%ENDOF DELEGATE SECTION

    private async loginDialogRoutine(customParams: PrompLoginCustomParameters = null): Promise<SessionObject> {
        const cur = this.getCurrentSession();
        const loginRequired = !(cur && cur.role == UserRole.AUTHENTICATED_USER);
        const collectAction = customParams?.cta?.action && inEnum(customParams.cta.action,CTA_NAME)?customParams.cta.action:null;
        if(loginRequired && 'fbq' in window && isFunction(window.fbq)) {
            window.fbq('trackCustom', 'login_required');
        }
        //this.statsCollector.collectBuyMembershipButtonClick(loginRequired);

        if(cur && cur.role == UserRole.AUTHENTICATED_USER) {
            const sobj = await this.getCurrentSessionObject();
            return sobj;
        }
        else {
            const gui = await this.getGui();
            const list = await getAuthProviderButtonsToShow(this, false);
            const _preloadProvs = await this.loginManager.preloadProviders(list,false);
            if(collectAction) {
                this.statsCollector.collectCTAData({
                    action: collectAction,
                    outcome: CTA_OUTCOME.SHOWN,
                    source_element: customParams?.cta?.source_element?customParams?.cta?.source_element:"unspecified"
                });
            }
            return gui.generateLoginDialog<SessionObject>(list, (pid: AuthProviderIdentifier, othParams: Partial<PrompLoginCustomParameters>) => {
                        const useParams = {
                            ...customParams,
                            ...othParams
                        };
                        if (!isDismissedCTA(pid)) {
                            try {
                                if(collectAction) {
                                    this.statsCollector.collectCTAData({
                                        action: collectAction,
                                        outcome: CTA_OUTCOME.COMPLETED,
                                        source_element: customParams?.cta?.source_element?customParams?.cta?.source_element:"unspecified",
                                        provider: pid
                                    })
                                }
                            }
                            catch(e) {
                                console.error(e);
                            }
                            return this.login(pid, null, useParams).
                                then(x => {
                                    if(x != null && x.session != null) return Promise.resolve(x);
                                    else return Promise.reject("unable to generate a session from click")
                                })
                        }
                        else return Promise.resolve(null);
                    },() => {
                            if(collectAction) {
                                this.statsCollector.collectCTAData({
                                    action: collectAction,
                                    outcome: CTA_OUTCOME.DISMISSED,
                                    source_element: customParams?.cta?.source_element?customParams?.cta?.source_element:"unspecified"
                                })
                            } 
                    },{title: customParams?.title, subtitle: customParams?.subtitle});
                

        }

    }

    execCTA(): Promise<void> {
        if (!this.config.membership?.enable) {
            if (this.loginRequested()) {
                return this.loginDialogRoutine().then();
            }
            else {
                return this.ctaLogic.execCTA();
            }
        }
    }

    forceCTAAction(action: ManualAction): Promise<void> {
        return this.ctaLogic.forceActionExecution(action);
    }

    dismissCTA() {
        this.gsiLoader.cancelOneTapPrompt();
        this.ctaLogic.signalNonUserDismiss();
        if (this.autoloader.guiLoaded()) {
            return this.getGui().then(
                gui => {
                    gui.empty();
                }
            ).then();
        }
        return Promise.resolve();
    }


    private async handleMembershipBuyDirect(type: MembershipType, options: BuyMembershipOptions = {}) {
        const membershipConfig = this.config.membership;

        if (!membershipConfig.enable) {
            return Promise.reject("Unsupported method");
        }

        if (!membershipConfig.anonBuySupport || isGift(type)) {
            await this.loginDialogRoutine({
                // Questo serve in caso di login con email in modo che quando riatterriamo in pagina
                // abbiamo ancora il parametro. Quando il login email sarà basato su codice (quindi mantenendo l'utente in pagina) si potrà eliminare questo parametro
                returnUri: options.returnUrl
            });
            const curs = this.getCurrentSession();

            if (curs && curs.role == UserRole.AUTHENTICATED_USER) {
                this.redirectToDirectProductBuyPage(type, options);
            }
            else {
                console.log("[APPLE] Redirect avoided for unlogged user")
            }
        }
        else {
            this.redirectToDirectProductBuyPage(type, options);
        }
        
    }


    
    async showBuyDialog(returnUrl?: string, flags?: MembershipDialogFlag[] ,options?: {
            title?: string,
            subtitle?: string,
            buttonText?: string,
            cta?: CTAStatsDetails
        }): Promise<SessionObject | null> {
        return new Promise<SessionObject | null>(async (resolveLogin, reject) => {
            const useFlags = !isYearSub(flags) && !isMonthSub(flags)?MembershipFlagsBasicConf:flags;

            const gui = await this.getGui();
            const cur = this.getCurrentSession();
            const loginRequired = !(cur && cur.role == UserRole.AUTHENTICATED_USER);
            const collectAction = options?.cta?.action && inEnum(options.cta.action,CTA_NAME)?options.cta.action:null;

            if(collectAction) {
                this.statsCollector.collectCTAData({
                    action: collectAction,
                    outcome: CTA_OUTCOME.SHOWN,
                    source_element: options?.cta?.source_element?options?.cta?.source_element:"unspecified"
                });
            }

            const self = this;
            function eventOnClick(mt: MembershipType): void {
                if(collectAction) {
                    self.statsCollector.collectCTAData({
                        action: collectAction,
                        outcome: CTA_OUTCOME.COMPLETED,
                        source_element: options?.cta?.source_element?options?.cta?.source_element:"unspecified",
                        membership_type: mt
                    });
                }
            }

            await gui.generateBuyMembershipDialog(
                () => {
                    eventOnClick(MembershipType.SELF_MONTHLY);
                    return this.handleBuyPersonalMembershipMonth({
                        returnUrl: returnUrl,
                        useDiscount: isDiscount(useFlags),
                        useFreeTrial: isFreeTrial(useFlags)
                    });
                },
                () => {
                    eventOnClick(MembershipType.SELF_YEARLY);
                    return this.handleBuyPersonalMembershipYear({
                        returnUrl: returnUrl,
                        useDiscount: isDiscount(useFlags),
                        useFreeTrial: isFreeTrial(useFlags)
                    });
                },
                {
                    loginAction: async () => {
                        try {
                            const maybeSession = await this.showLoginDialog(returnUrl);
                            resolveLogin(maybeSession);
                        } catch(err) {
                            resolveLogin(null);
                        }
                    },
                    showLogin: loginRequired,
                    buttonText: options?.buttonText,
                    title: options?.title,
                    subtitle: options?.subtitle,
                    yearlyOnly: isYearSub(useFlags) && !isMonthSub(useFlags),
                    discount: isDiscount(useFlags),
                    freeTrial: isFreeTrial(useFlags)
                }
            );
        });
    }



    
    
    async showMailFixOrLoginDialog(isFix: boolean, 
        sourceIntent:  UserActionIdentifier,
        afterRegistrationCallback: (d: SubscriptionCallbackData) => void,
        mailRedirectUrl?: string,
        topicLabel?: string,
        options?: {
            cta?: CTAStatsDetails
        }
    ): Promise<MailFixDialogOutcome> {
        

        if(options && options.cta && topicLabel) options.cta.topic = topicLabel;
        try {
            const userData = await this.internalShowMailLoginDialog(isFix,sourceIntent,options);
            const purpose = isFix?Ask1stPartyEmailLoginOperation.VERIFICATION:Ask1stPartyEmailLoginOperation.SIGN_UP;
            const det = this.detectedWebview;
            if(det && det.native_app) {
                userData.native_app = det.platform.android? EmailLoginSourceContext.ANDROID_APP : EmailLoginSourceContext.IOS_APP;
            }
            if(mailRedirectUrl) userData.redirect_uri = mailRedirectUrl;

            const askRes = await this.ask1stPartyEmailLogin(purpose,userData);
            
            return this.externalMailFixOrLoginSubmit(askRes, userData, sourceIntent, afterRegistrationCallback, options);
        }catch(err) {
            console.error("Error during mail fix/login dialog",err);
            return MailFixDialogOutcome.USER_CLOSED_DIALOG;
        }
        
    }


    async  externalMailFixOrLoginSubmit(askRes: EmailLoginAskResponse,
        userData: Ask1stPartyEmailAuthData, 
        sourceIntent: UserActionIdentifier,
        afterRegistrationCallback: (d: SubscriptionCallbackData) => void,
        options?: {
            cta?: CTAStatsDetails
        }): Promise<MailFixDialogOutcome> {
            const collectAction = options?.cta?.action && inEnum(options.cta.action,CTA_NAME)?options.cta.action:null;
            if(collectAction) {
                    this.statsCollector.collectCTAData({
                    action: collectAction,
                    outcome: CTA_OUTCOME.COMPLETED,
                    source_element: options?.cta?.source_element?options?.cta?.source_element:"unspecified",
                    topic: options.cta?.topic?options.cta.topic:null
                });
            }
            let outcome = MailFixDialogOutcome.WAITING_LINK_CLICK;
            let otpProcedure: SessionObject | OTPVerificationResponse = null;
            if(askRes.challenge && this.config.loginManager?.actual_providers?.email?.codeLoginAllowed) {
                try {
                    otpProcedure = await (await this.getGui()).otpLoginProcedure(
                        {
                            email: userData.email,
                            firstName: userData?.first_name,
                            lastName: userData?.last_name
                            //redirect_uri: userData?.redirect_uri
                        },
                        this,
                        askRes.purpose?askRes.purpose:Ask1stPartyEmailLoginOperation.SIGN_UP,
                        askRes
                   )
                } catch(ex) {
                    console.error("Error during OTP procedure",ex);
                    outcome = MailFixDialogOutcome.USER_CLOSED_DIALOG;
                }
            }
            if(otpProcedure instanceof SessionObject && isLoggedInSession(otpProcedure.session)) {
                outcome = MailFixDialogOutcome.LOGIN_COMPLETED_VIA_OTP;
            }
            else if (isOTPVerificationResponse(otpProcedure)) {
                outcome = MailFixDialogOutcome.VERIFICATION_COMPLETED_VIA_OTP;
            }
            
            if(outcome != MailFixDialogOutcome.USER_CLOSED_DIALOG ) {
                
                if(afterRegistrationCallback) { 
                            
                    try {
                        afterRegistrationCallback({
                            user: {
                                first_name: userData.first_name,
                                last_name: userData.last_name,
                                email: userData.email
                            },
                            subscription_intent: true,
                            is_subscription_changed: true,
                            topic_label: options?.cta?.topic?options.cta.topic:null,
                            user_was_already_registered: false
                        });
                    } catch(ex) {
                        console.error("Error while executing afterRegistrationCallback");
                        console.error(ex);
                    }
                }
                // else {
                //     this.showThankYouForRegistrationDialog(sourceIntent, userData.email, outcome == MailFixDialogOutcome.WAITING_LINK_CLICK, options?.cta?.topic?options.cta.topic:null);
                // }
            }

            return outcome;
    }


    async showThankYouForRegistrationDialog(sourceIntent: UserActionIdentifier, email: string, youWereMailed: boolean = true, topicLabel?: string) {
        
        let baseMessage = null;
        let message = null;
        if(sourceIntent == UserActionIdentifier.NEWSLETTER_SUBSCRIBE) {
                baseMessage = (sourceIntent == UserActionIdentifier.NEWSLETTER_SUBSCRIBE ? "Grazie, ora sei iscritto a":"") + (topicLabel ? ` ${topicLabel}!` : "lla newsletter.") ;
                message = youWereMailed?`Ti abbiamo inviato un'email per confermare la tua iscrizione.`:"";
        }    
        else if(youWereMailed) {
            baseMessage = "Grazie";
            message = "Ti abbiamo inviato una mail di conferma";
        }
        
        if(baseMessage) {
            const gui = await this.getGui();
            gui.generateThankYouDialog(baseMessage, message);
        }
    }

    async displayMessageDialog(title: string, subtitle?: string) {
        const gui = await this.getGui();
        gui.generateThankYouDialog(title,subtitle?subtitle:"");
    }
    

    //?TODO: è stata resa selezionabile ma va gestita la catena
    // rimuovendo i redirect enumerativi anche nello showBuyDialog
    // va comunicato a wordpress di passare correttamente il parametro
    showLoginDialog(return_url?: string, options?: {title?: string,subtitle?: string,cta?: CTAStatsDetails}): Promise<SessionObject> {
        
        // return this.loginDialogRoutine({
        //     returnUri: AfterLoginRedirectMode.AGGIORNAMENTI
        // });
        return this.loginDialogRoutine({
            returnUri: return_url,
            title: options?.title,
            subtitle: options?.subtitle,
            cta: options?.cta
        });

    }
    handleBuyPersonalMembershipMonth(options: BuyMembershipOptions = {}) {
        this.handleMembershipBuyDirect(MembershipType.SELF_MONTHLY, options);
    }
    handleBuyPersonalMembershipYear(options: BuyMembershipOptions = {}) {
        this.handleMembershipBuyDirect(MembershipType.SELF_YEARLY, options);
    }
    handleBuyGiftMembershipMonth(options: BuyMembershipOptions = {}) {
        this.handleMembershipBuyDirect(MembershipType.GIFT_MONTHLY, options);
    }
    handleBuyGiftMembershipYear(options: BuyMembershipOptions = {}) {
        this.handleMembershipBuyDirect(MembershipType.GIFT_YEARLY, options);
    }
    async handleMembershipSwitchToYearly(options: Omit<Omit<BuyMembershipOptions, "useDiscount">, "useFreeTrial"> = {}) {
        const link = await this.api.getMembershipSwitchLink(options.returnUrl);
        if (options.newTab) {
            window.open(link, '_blank').focus();
        }
        else {
            window.location.href = link;
        }
        
    }

    private handleMembershipReauth(): Promise<void> {
        this.logger.debug("Handling reauth");
        return this.logout().then( () => this.showLoginDialog().then(() => window.location.reload()));
    }

    /**
     * Restituisce una promise bool, la promise è true se è stata lanciata la dialog per richiedere il relogin, false altrimenti
     */
    private membershipReauthRoutine(): Promise<boolean> {
        if (this.supportsMembership()) {
            return this.isUnloggedMember().then(
                unlogged => {
                    if (unlogged) {
                        this.logger.log("Unlogged member detected");
                        return this.getGui().then(
                            gui => {
                                gui.generateMembershipReauthUnderfollow(() => this.handleMembershipReauth());
                                return true;
                            }
                        ).catch(() => false);
                    }
                    else {
                        return Promise.resolve(false);
                    }
                }
            ).catch(() => false)
        }
        else {
            return Promise.resolve(false);
        }
    }

    private getMeCache: Promise<Response<UserFullInfo, any>> = null;
    
    /**
     * getMe local cache
     * @param full_data 
     * @returns 
     */
    getMe(full_data?: boolean): Promise<Response<UserFullInfo, any>> {
        if (this.getMeCache == null) {
            this.getMeCache = this.api.getMe(true);
            this.getMeCache.catch( () => this.getMeCache = null )
        }
        
        return this.getMeCache;
    }



    public gotoUrlAfterLogin(url: string, target: LinkTarget = LinkTarget.BLANK, options?: {
        dialog_title?: string,
        dialog_subtitle?: string,
        dialog_button?: string,
        utm_source?: string,
        utm_medium?: string
    }, membershipFlags?: MembershipDialogFlag[], 
       extent?: LoginExtent,
       overrideBarrierDisplayMode?: BarrierDisplayMode
    ): Promise<void> {
        
        const uopts = options || {};
        //TODO: add tracking?
        return this.loginBarrierRestServices.executeWithBarrierAndOptions((barr) => {
            window.setTimeout(() => {
                if(target == LinkTarget.SELF) {
                    document.location.href = url;
                }
                else {
                    window.open(url,target);
                }
            });
            return Promise.resolve();  
        }, {
            ...uopts,
            redirect_url: url
        },membershipFlags,extent,overrideBarrierDisplayMode);
    }

}